// src/components/ModernRecipeCard.tsx
import React from 'react';
import { Recipe, RecipeSource } from '@/types/recipe';
import { Button } from '@/components/ui/button';
import { Star, Eye, EyeOff } from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { RECIPE_SOURCE_CONFIG, DEFAULT_SOURCE_CONFIG } from '@/config/recipeSourceConfig';
import { chefPersonas } from '@/config/chefPersonas';

interface ModernRecipeCardProps {
  recipe: Recipe;
  onClick: () => void;
  onFavoriteToggle: () => void;
  onShareToggle: () => void;
  onDelete?: () => void;
  isAdmin: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const SourceIcon: React.FC<{ source?: RecipeSource; tags?: string | string[] }> = ({ source, tags }) => {
  if (!source) return null;

  if (source === RecipeSource.Studio) {
    let chefId: string | undefined;
    try {
      const tagArray = typeof tags === 'string' ? JSON.parse(tags) : tags;
      if (Array.isArray(tagArray)) {
        chefId = tagArray.find(t => chefPersonas.some(p => p.id === t));
      }
    } catch {
    }

    const persona = chefPersonas.find(p => p.id === chefId);

    if (persona) {
      return (
        <div
          className="absolute bottom-2 right-2 z-10 transition-transform duration-300 group-hover:scale-110"
          title={`Created by ${persona.name}`}
        >
          <div className="h-10 w-10 rounded-full border-2 border-white dark:border-stone-900 bg-white/90 dark:bg-stone-900/90 shadow-lg overflow-hidden flex items-center justify-center">
            <img
              src={persona.avatar}
              alt={persona.name}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = '<div class="text-terracotta-500"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-sparkles"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg></div>';
              }}
            />
          </div>
        </div>
      );
    }
  }

  const { icon: Icon, color, tooltip } = RECIPE_SOURCE_CONFIG[source] || DEFAULT_SOURCE_CONFIG;
  return (
    <div
      className="absolute bottom-3 right-3 z-10"
      title={tooltip}
    >
      <div className="h-7 w-7 rounded-full bg-white/90 dark:bg-stone-900/90 flex items-center justify-center shadow-sm">
        <Icon className={`h-4 w-4 ${color}`} />
      </div>
    </div>
  );
};

const ModernRecipeCard: React.FC<ModernRecipeCardProps> = ({
  recipe,
  onClick,
  onFavoriteToggle,
  onShareToggle,
  onDelete: _onDelete,
  isAdmin: _isAdmin = false,
  size: _size = 'md'
}) => {
  const { role } = useAuth();
  const defaultPublicImageUrl = 'https://bzvzoqvyxfrgaxsokfva.supabase.co/storage/v1/object/public/recipeimage/chezalex.jpg';
  const imageUrl = recipe.image_url || defaultPublicImageUrl;

  return (
    <article
      className="group relative flex flex-col cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-500 focus-visible:ring-offset-2 rounded-xl"
      onClick={onClick}
      tabIndex={0}
      role="button"
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {/* Image Container */}
      <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-stone-100 dark:bg-stone-800">
        {/* Gradient overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent z-[1] opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

        {/* Logo watermark */}
        <div className="absolute top-3 left-3 z-10">
          <img
            src="/Cheflab_b.png"
            alt=""
            className="h-6 w-auto object-contain opacity-70 drop-shadow-sm pointer-events-none"
          />
        </div>

        {/* Main image with smooth zoom on hover */}
        <img
          src={imageUrl}
          alt={recipe.title}
          className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
          loading="lazy"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            if (target.src !== defaultPublicImageUrl) {
              target.src = defaultPublicImageUrl;
            }
          }}
        />

        {/* Action buttons - top right cluster */}
        <div className="absolute top-3 right-3 z-20 flex items-center gap-2">
          {/* Visibility toggle */}
          {(role === 'admin' || recipe.isOwner) && (
            <Button
              variant="ghost"
              size="icon"
              className={`h-9 w-9 rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 ${recipe.share
                  ? 'bg-white/90 dark:bg-stone-800/90 text-sage-600 dark:text-sage-400 hover:bg-white dark:hover:bg-stone-800'
                  : 'bg-white/90 dark:bg-stone-800/90 text-stone-400 dark:text-stone-500 hover:bg-white dark:hover:bg-stone-800'
                }`}
              onClick={e => { e.stopPropagation(); onShareToggle(); }}
              aria-label={recipe.share ? "Visible to others" : "Hidden from others"}
              title={recipe.share ? 'Shared' : 'Private'}
            >
              {recipe.share ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </Button>
          )}

          {/* Favorite star */}
          <Button
            variant="ghost"
            size="icon"
            className={`h-9 w-9 rounded-full shadow-lg backdrop-blur-sm transition-all duration-200 bg-white/90 dark:bg-stone-800/90 hover:bg-white dark:hover:bg-stone-800 ${recipe.isFavorite ? 'text-terracotta-500' : 'text-stone-400 dark:text-stone-500'
              }`}
            onClick={e => { e.stopPropagation(); onFavoriteToggle(); }}
            aria-label={recipe.isFavorite ? "Remove from favorites" : "Add to favorites"}
          >
            <Star className={`h-4 w-4 transition-all duration-200 ${recipe.isFavorite ? 'fill-terracotta-400 scale-110' : ''}`} />
          </Button>
        </div>

        {/* Category pill */}
        {recipe.category && (
          <div className="absolute bottom-3 left-3 z-10">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-white/90 dark:bg-stone-900/90 text-stone-700 dark:text-stone-200 shadow-sm backdrop-blur-sm">
              {recipe.category}
            </span>
          </div>
        )}

        {/* Source icon */}
        <SourceIcon source={recipe.source} tags={recipe.tags} />
      </div>

      {/* Content */}
      <div className="pt-3 pb-1 px-0.5 flex flex-col flex-grow">
        {/* Title */}
        <h3 className="font-semibold text-base leading-snug line-clamp-2 text-stone-900 dark:text-stone-100 group-hover:text-terracotta-600 dark:group-hover:text-terracotta-400 transition-colors duration-200">
          {recipe.title}
        </h3>

        {/* Description */}
        {recipe.description && (
          <p className="mt-1.5 text-sm text-stone-500 dark:text-stone-400 line-clamp-2 leading-relaxed">
            {recipe.description}
          </p>
        )}

        {/* Footer metadata */}
        <div className="flex items-center justify-between mt-auto pt-3 text-xs text-stone-400 dark:text-stone-500">
          <time dateTime={recipe.dateCreated}>
            {format(new Date(recipe.dateCreated), 'MMM d, yyyy')}
          </time>

          {recipe.isOwner ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-terracotta-50 text-terracotta-700 dark:bg-terracotta-900/30 dark:text-terracotta-300">
              Your Recipe
            </span>
          ) : recipe.creatorName ? (
            <div className="flex items-center gap-1.5">
              {recipe.creatorCountry && (
                <CountryFlag
                  countryCode={recipe.creatorCountry}
                  size="sm"
                  className="flex-shrink-0 opacity-70"
                />
              )}
              <span className="text-stone-500 dark:text-stone-400">
                by {recipe.creatorName}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default ModernRecipeCard;
